import React, { useState, useEffect, useCallback } from "react";
import "../../CallDetails.css";

const isTranscriptArray = (transcript) => Array.isArray(transcript);

// Special phone numbers that indicate RIA calls
const SPECIAL_PHONE_NUMBERS = ['40373800880', '+40373800880'];

// Check if a phone number is a RIA number
const isRiaCall = (fromNumber, toNumber) => {
  const checkNumber = (num) => {
    if (!num) return false;
    const normalized = num.replace(/\s+/g, '');
    return SPECIAL_PHONE_NUMBERS.some(special => 
      normalized === special || normalized.endsWith(special)
    );
  };
  return checkNumber(fromNumber) || checkNumber(toNumber);
};

const CallDetailsPage = ({ callId, onBack, onViewAllWithNumber }) => {
  const [call, setCall] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCallDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/calls/${callId}`);
      if (response.ok) {
        const data = await response.json();
        setCall(data);
        setUser(data.user);
      } else {
        setError("Failed to load call details");
      }
    } catch (error) {
      console.error("Error fetching call details:", error);
      setError("Failed to load call details");
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    fetchCallDetails();
  }, [callId, fetchCallDetails]);

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Normalize status to underscore format
  const normalizeStatus = (status) => status ? status.replace(/-/g, '_') : '';

  const getCallTypeLabel = (call) => {
    const status = normalizeStatus(call.status);
    if (status === "missed" || status === "missed_inbound") return "Missed Call";
    if (call.direction === "inbound") return "Inbound Call";
    if (call.direction === "outbound") return "Outbound Call";
    return "Call";
  };

  const getCallTypeBadge = (call) => {
    const status = normalizeStatus(call.status);
    let className = "call-type-badge";
    let icon = "";
    
    if (status === "missed" || status === "missed_inbound") {
      className += " missed";
      icon = "📞";
    } else if (call.direction === "inbound") {
      className += " inbound";
      icon = "📞";
    } else if (call.direction === "outbound") {
      className += " outbound";
      icon = "📞";
    }
    
    return (
      <span className={className}>
        {icon} {getCallTypeLabel(call)}
      </span>
    );
  };

  const getUserInitials = (name) => {
    if (!name) return "U";
    // Convert to string in case it's a number (like user_id)
    const nameStr = String(name);
    return nameStr.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderUserInfo = () => {
    // If we have complete user data, show name and ID
    if (user && user.full_name) {
      return (
        <div className="user-info">
          <div className="user-avatar">
            {getUserInitials(user.full_name)}
          </div>
          <span>{user.full_name} ({user.id})</span>
        </div>
      );
    }
    
    // If we have user_id but no user object, show ID only (user lookup failed)
    if (call.user_id) {
      return (
        <div className="user-info">
          <div className="user-avatar">
            {getUserInitials(call.user_id)}
          </div>
          <span>Agent ID: {call.user_id}</span>
        </div>
      );
    }
    
    return <span style={{ color: '#666', fontStyle: 'italic' }}>Unknown</span>;
  };

  if (loading) {
    return (
      <div className="call-details-container">
        <div className="loading">Loading call details...</div>
      </div>
    );
  }

  if (error || !call) {
    return (
      <div className="call-details-container">
        <div className="details-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        </div>
        <div className="error">{error || "Call not found"}</div>
      </div>
    );
  }

  const otherNumber = call.direction === "inbound" ? call.from_number : call.to_number;

  return (
    <div className="call-details-container">
      <div className="details-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h2>{getCallTypeLabel(call)}</h2>
      </div>

      <div className="details-content">
        <div className="details-section">
          <h3>
            <svg className="section-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            Call Information
          </h3>
          <div className="detail-row">
            <span className="detail-label">Type:</span>
            <span className="detail-value">{getCallTypeBadge(call)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">From:</span>
            <span className="detail-value">{call.from_number}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">To:</span>
            <span className="detail-value">{call.to_number}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Agent:</span>
            <span className="detail-value">{renderUserInfo()}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Date:</span>
            <span className="detail-value">{formatDate(call.started_at)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Duration:</span>
            <span className="detail-value">{formatDuration(call.duration_seconds)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Status:</span>
            <span className="detail-value">{call.status}</span>
          </div>
        </div>

        {/* RIA Metadata Section - Only show for RIA calls with metadata */}
        {isRiaCall(call.from_number, call.to_number) && call.metadata && (
          <div className="details-section ria-metadata-section">
            <h3>
              <span className="ria-badge-header">RIA</span>
              Call Metadata
            </h3>
            <div className="detail-row">
              <span className="detail-label">Lead ID:</span>
              <span className="detail-value ria-lead-id">
                {call.metadata.lead_id || '-'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Call Attempt:</span>
              <span className="detail-value">
                {/* New format: call_attempt object */}
                {call.metadata.call_attempt ? (
                  <span className="call-attempts-info">
                    <span className="attempt-stat attempt-label">
                      {call.metadata.call_attempt.label || `Call #${call.metadata.call_attempt.attempt_number || 1}`}
                    </span>
                    <span className="attempt-divider">•</span>
                    <span className="attempt-stat">
                      Day {call.metadata.call_attempt.day ?? 0}
                    </span>
                    {call.metadata.call_attempt.total_attempts_before > 0 && (
                      <>
                        <span className="attempt-divider">•</span>
                        <span className="attempt-stat attempt-previous">
                          {call.metadata.call_attempt.total_attempts_before} previous
                        </span>
                      </>
                    )}
                  </span>
                ) : /* Old format: call_attempts object */
                call.metadata.call_attempts ? (
                  <span className="call-attempts-info">
                    <span className="attempt-stat">
                      Today: {call.metadata.call_attempts.current_daily_attempt || 0}
                    </span>
                    <span className="attempt-divider">•</span>
                    <span className="attempt-stat">
                      Total: {call.metadata.call_attempts.total_previous_attempts || 0}
                    </span>
                  </span>
                ) : '-'}
              </span>
            </div>
            {call.metadata.is_first_call !== undefined && (
              <div className="detail-row">
                <span className="detail-label">First Call:</span>
                <span className="detail-value">
                  <span className={`first-call-badge ${call.metadata.is_first_call ? 'yes' : 'no'}`}>
                    {call.metadata.is_first_call ? 'Yes' : 'No'}
                  </span>
                </span>
              </div>
            )}
            {call.metadata.verification_link && (
              <div className="detail-row">
                <span className="detail-label">Verification:</span>
                <span className="detail-value">
                  <a 
                    href={call.metadata.verification_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="verification-link"
                  >
                    Open Verification Link
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </span>
              </div>
            )}
          </div>
        )}

        {call.recording_url && (
          <div className="details-section">
            <h3>
              <svg className="section-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
              </svg>
              Recording
            </h3>
            {call.recording_url === "pending" ? (
              <div className="pending-message">Recording is being processed...</div>
            ) : (
              <audio controls className="recording-player">
                <source src={call.recording_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            )}
          </div>
        )}

        {call.openai_transcript && (
          <div className="details-section transcript-section">
            <h3>
              <svg className="section-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              Transcript (OpenAI)
            </h3>
            {call.openai_transcript === "pending" ? (
              <div className="pending-message">AI transcript is being generated...</div>
            ) : isTranscriptArray(call.openai_transcript) ? (
              <div className="transcript-chat">
                {call.openai_transcript.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`chat-bubble ${msg.speaker === 'agent' ? 'right' : 'left'}`}
                  >
                    <span className="chat-speaker">{msg.speaker === 'agent' ? 'Agent' : 'Caller'}</span>
                    <span className="chat-text">{msg.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="transcript-text">{call.openai_transcript}</div>
            )}
          </div>
        )}

        <div className="details-actions">
          <button
            className="action-button"
            onClick={() => onViewAllWithNumber(otherNumber)}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            View all calls with {otherNumber}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallDetailsPage; 